import { NavLink } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { ChartCard } from "../components/ChartCard";
import { ChartTooltipContent } from "../components/ChartTooltipContent";
import { DataTableCard } from "../components/DataTableCard";
import { KpiRail } from "../components/KpiRail";
import { NumberedListCard } from "../components/NumberedListCard";
import { PremiumHero } from "../components/PremiumHero";
import { SignalPill } from "../components/SignalPill";
import { StatusChip } from "../components/StatusChip";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";

type RiskPageData = Awaited<ReturnType<typeof api.getRisk>>;

function buildRiskRows(data: RiskPageData) {
  return [
    ["Recommendation", data.recommendation.label, data.recommendation.confidencePosture],
    ["Overall risk posture", data.riskSummary.riskPosture, "Continuation is conditional, not blind"],
    ["Legal gate", data.riskSummary.gateSummary.legal, "Explicit downside control remains live"],
    ["Site gate", data.riskSummary.gateSummary.site, "Parcel and regulatory fit still matter"],
    ["Revenue gate", data.riskSummary.gateSummary.revenue, "Commercial downside still has to stay bounded"],
    [
      "Risk-adjusted return gate",
      data.riskSummary.gateSummary.riskAdjustedReturn,
      "Return discipline remains a hard stop"
    ]
  ];
}

function buildImprovementRows(data: RiskPageData) {
  return data.riskSummary.whatImproved.map((item, index) => [
    `Improvement ${index + 1}`,
    item,
    data.recommendation.topPositives[index] ?? "Supports managed continuation"
  ]);
}

function buildOpenRiskRows(data: RiskPageData) {
  return [
    ...data.riskSummary.remainingOpenRisks.map((item, index) => [
      `Open risk ${index + 1}`,
      item,
      "Still active"
    ]),
    ...data.recommendation.topCautions.map((item, index) => [
      `Caution ${index + 1}`,
      item,
      "Still active"
    ])
  ];
}

function buildKillRows(data: RiskPageData) {
  return data.riskSummary.killCriteriaSensitivity.map((item, index) => [
    `Kill sensitivity ${index + 1}`,
    item,
    "Continuation fails if this breaks the control stack"
  ]);
}

function buildContinuationRows(data: RiskPageData) {
  return data.recommendation.why.map((item, index) => [`Support ${index + 1}`, item]);
}

function RiskContent({ data }: { data: RiskPageData }) {
  const pressureData = [
    { name: "Improved", value: data.riskSummary.whatImproved.length },
    { name: "Open", value: data.riskSummary.remainingOpenRisks.length },
    { name: "Cautions", value: data.recommendation.topCautions.length },
    { name: "Kill", value: data.riskSummary.killCriteriaSensitivity.length }
  ];
  const totalVisibleControls =
    data.riskSummary.remainingOpenRisks.length + data.riskSummary.killCriteriaSensitivity.length;
  const riskRows = buildRiskRows(data);
  const improvementRows = buildImprovementRows(data);
  const openRiskRows = buildOpenRiskRows(data);
  const killRows = buildKillRows(data);
  const continuationRows = buildContinuationRows(data);

  return (
    <div className="stack-xl">
      <PremiumHero
        eyebrow="Downside Control"
        title="Risk is visible, bounded, and still governable."
        subtitle="The project is not being sold as risk-free. It is being carried because the downside is now compact enough to govern: what improved is visible, what remains open is explicit, and the kill triggers are still live."
        backgroundImage="/comps/110-f-st-map.jpg"
        backgroundLabel="Grounded local context map"
        badges={
          <>
            <SignalPill label={data.riskSummary.riskPosture} tone="warning" />
            <SignalPill label={data.recommendation.label} tone="neutral" />
            <SignalPill label={`${data.riskSummary.killCriteriaSensitivity.length} live kill triggers`} tone="warning" />
          </>
        }
        actions={
          <>
            <NavLink to="/financials" className="link-button link-button-primary">
              Open Financials
            </NavLink>
            <NavLink to="/site-zoning" className="link-button link-button-secondary">
              Open Site & Zoning
            </NavLink>
          </>
        }
        aside={
          <div className="stack-md">
            <div className="stack-xs">
              <span className="card-eyebrow">At a Glance</span>
              <div style={{ fontSize: "1.8rem", lineHeight: 1, color: "#f6fbff", letterSpacing: "-0.04em" }}>
                {data.recommendation.label}
              </div>
              <p style={{ margin: 0, color: "rgba(223,233,248,0.78)", lineHeight: 1.5 }}>
                {data.riskSummary.riskPosture}
              </p>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "12px"
              }}
            >
              {[
                ["What improved", `${data.riskSummary.whatImproved.length}`],
                ["Open risks", `${data.riskSummary.remainingOpenRisks.length}`],
                ["Kill triggers", `${data.riskSummary.killCriteriaSensitivity.length}`],
                ["Visible controls", `${totalVisibleControls}`]
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    borderRadius: "18px",
                    padding: "14px",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)"
                  }}
                >
                  <div style={{ fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(198,211,234,0.62)" }}>
                    {label}
                  </div>
                  <div style={{ marginTop: "8px", color: "#f4f8ff", lineHeight: 1.35 }}>{value}</div>
                </div>
              ))}
            </div>
            <div className="row-wrap gap-sm">
              <StatusChip label={data.recommendation.confidencePosture} tone="neutral" />
              <StatusChip label={data.riskSummary.gateSummary.riskAdjustedReturn} tone="warning" />
            </div>
          </div>
        }
      />

      <KpiRail
        items={[
          {
            label: "Risk posture",
            value: data.riskSummary.riskPosture,
            note: "Top-line downside read",
            tone: "warning"
          },
          {
            label: "Recommendation",
            value: data.recommendation.label,
            note: data.recommendation.confidencePosture,
            tone: "neutral"
          },
          {
            label: "Open risks",
            value: `${data.riskSummary.remainingOpenRisks.length}`,
            note: "Visible unresolved items",
            tone: "warning"
          },
          {
            label: "Kill triggers",
            value: `${data.riskSummary.killCriteriaSensitivity.length}`,
            note: "Explicit continuation stops",
            tone: "warning"
          }
        ]}
      />

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
        }}
      >
        <ChartCard
          eyebrow="Risk Pressure"
          title="The downside stack is concentrated, not diffuse"
          description="This page keeps the risk picture visible in a few clear buckets: what improved, what remains open, what caution still exists, and where the kill triggers still sit."
        >
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pressureData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="name" tick={{ fill: "#d7e4f8", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "#9eb6d7", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltipContent title="Visible risk count" valueSuffix=" items" />} />
                <Bar dataKey="value" name="Items" fill="#ffcf7d" radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <DataTableCard
          eyebrow="Risk Control Matrix"
          title="The downside frame in one table"
          description="This is the top-level investor read: current recommendation, risk posture, and the four live continuation gates."
          columns={["Layer", "Current read", "Control role"]}
          rows={riskRows}
        />
      </div>

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
        }}
      >
        <DataTableCard
          eyebrow="What Improved"
          title="Mitigants and narrowed downside"
          description="Improvement stays visible here because the current case depends on real narrowing of the risk stack, not softer language."
          columns={["Improvement", "Current read", "Why it matters now"]}
          rows={improvementRows}
        />

        <DataTableCard
          eyebrow="What Still Requires Control"
          title="Active open-risk table"
          description="These items remain live. The page keeps them explicit so continuation reads as governed, not casual."
          columns={["Item", "Current read", "Status"]}
          rows={openRiskRows}
        />
      </div>

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
        }}
      >
        <DataTableCard
          eyebrow="Kill Conditions"
          title="Continuation stops remain explicit"
          description="These are still active by design. The project is only investable if these sensitivities stay inside the control stack."
          columns={["Gate", "Current read", "Meaning"]}
          rows={killRows}
          footer={
            <div className="row-wrap gap-sm">
              <StatusChip label={data.riskSummary.gateSummary.legal} tone="warning" />
              <StatusChip label={data.riskSummary.gateSummary.site} tone="warning" />
              <StatusChip label={data.riskSummary.gateSummary.revenue} tone="warning" />
            </div>
          }
        />

        <DataTableCard
          eyebrow="Why Continuation Still Holds"
          title="Managed-risk interpretation"
          description="Continuation remains justified because the current recommendation still has concrete support, even with active gates and unresolved items still in view."
          columns={["Support", "Current read"]}
          rows={continuationRows}
          footer={
            <div style={{ color: "rgba(216, 227, 244, 0.68)", fontSize: "0.88rem" }}>
              Interpretation: continue under discipline, not under optimism.
            </div>
          }
        />
      </div>

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))"
        }}
      >
        <NumberedListCard
          eyebrow="Improvement Read"
          title="What has already been pulled under control"
          items={data.riskSummary.whatImproved}
          tone="positive"
        />
        <NumberedListCard
          eyebrow="Open Control Agenda"
          title="What still has to stay governed"
          items={data.riskSummary.remainingOpenRisks}
          tone="warning"
        />
      </div>

      <section
        style={{
          borderRadius: "24px",
          padding: "22px",
          background: "linear-gradient(180deg, rgba(9, 14, 26, 0.92), rgba(7, 11, 21, 0.82))",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 18px 42px rgba(3, 8, 20, 0.24)"
        }}
      >
        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
            alignItems: "center"
          }}
        >
          <div className="stack-xs">
            <span className="card-eyebrow">Supporting Detail</span>
            <h3 className="card-title">Open Financials and Site & Zoning only after the top-level downside read is clear.</h3>
            <p className="card-copy">
              This page now carries the investor-facing downside synthesis. Financials and Site & Zoning remain supporting review layers for the specific gates still controlling the answer.
            </p>
          </div>
          <div className="row-wrap gap-sm">
            <NavLink to="/financials" className="link-button link-button-secondary">
              Financials
            </NavLink>
            <NavLink to="/site-zoning" className="link-button link-button-secondary">
              Site & Zoning
            </NavLink>
          </div>
        </div>
      </section>
    </div>
  );
}

export function RiskPage() {
  const { data, loading, error } = useContent("risk-page", api.getRisk);

  return (
    <PageContainer
      eyebrow="Risks & Mitigants"
      title="Controlled downside. Explicit gates."
      lede="Risk posture, what improved, open controls, kill triggers, and continuation logic are compressed into one investor-readable downside screen."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading risks and mitigants...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? <RiskContent data={data} /> : null}
    </PageContainer>
  );
}
